(() => {
  if (window.SCProductDesktopLayout) return;

  const desktop = window.matchMedia('(min-width: 1001px)');
  const instances = new Map();
  let pending = 0;

  function refresh() {
    if (!pending) pending = requestAnimationFrame(reconcile);
  }

  class DesktopProductLayout {
    constructor(product) {
      this.product = product;
      this.section = product.closest('.product-page-section');
      this.scope = this.section.closest('.template-product-wrapper') || this.section.parentElement;
      this.layout = product.querySelector('.product-page__content');
      this.media = this.layout?.querySelector(':scope > media-gallery');
      this.info = this.layout?.querySelector(':scope > .product__info-wrapper');
      this.header = document.querySelector('.shopify-section-header');
      this.content = null;
      this.marker = null;
      this.frame = 0;
      this.suspended = false;
      this.queueMeasure = () => {
        if (!this.frame && !this.suspended) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.measure();
        });
      };
      this.resizeObserver = new ResizeObserver(this.queueMeasure);
      this.thumbnailObserver = new MutationObserver(() => this.revealThumbnail());
    }

    connect() {
      if (!this.layout || !this.media || !this.info) return;
      this.resizeObserver.observe(this.layout);
      if (this.header) {
        this.resizeObserver.observe(this.header);
        this.header.addEventListener('transitionend', this.queueMeasure);
      }
      this.thumbnailObserver.observe(this.media, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
      window.addEventListener('resize', this.queueMeasure);
      window.addEventListener('scroll', this.queueMeasure, { passive: true });
      this.product.addPreProcessCallback?.(() => this.suspend());
      this.product.postProcessHtmlCallbacks?.push(refresh);
      this.update();
    }

    update() {
      if (this.suspended || !this.layout || !this.media || !this.info) return;
      const content = this.scope.querySelector('.product-content-section');
      if (content !== this.content) {
        this.restoreContent();
        this.marker?.remove();
        this.content = content;
        this.marker = null;
        if (content) {
          this.marker = document.createComment('sc-product-content-position');
          content.before(this.marker);
        }
      }

      this.product.staticBuyTools = true;
      window.removeEventListener('scroll', this.product.onScrollToolsHandler);
      this.product.resizeProductInfoObserver?.disconnect();
      this.product.productInfoContainer?.classList.remove('product__info-container--modal', 'product__info-container--modal--out', 'background-modal');

      if (desktop.matches) {
        this.layout.classList.add('sc-product-desktop-layout');
        if (this.content && this.content.parentElement !== this.info) this.info.append(this.content);
      } else {
        this.restoreContent();
        this.layout.classList.remove('sc-product-desktop-layout');
        this.clearProperties();
      }
      this.queueMeasure();
    }

    restoreContent(includeDetached = false) {
      const available = this.content?.isConnected || (includeDetached && this.content && this.product.contains(this.content));
      if (available && this.marker?.isConnected && this.content.previousSibling !== this.marker) {
        this.marker.after(this.content);
      }
    }

    setProperty(name, value) {
      if (this.layout.style.getPropertyValue(name) !== value) this.layout.style.setProperty(name, value);
    }

    clearProperties() {
      ['--sc-product-sticky-top', '--sc-product-main-height', '--sc-product-thumbnail-height'].forEach(name => this.layout.style.removeProperty(name));
    }

    measure() {
      if (!desktop.matches || this.suspended || !this.product.isConnected || !this.layout.classList.contains('sc-product-desktop-layout')) return;
      const wrapper = this.media.querySelector('.product-single__wrapper');
      const group = this.media.querySelector('.product-single__media-group__wrap');
      if (!wrapper || !group) return;
      const headerBottom = Math.max(0, this.header?.getBoundingClientRect().bottom || 0);
      const stickyTop = Math.ceil(headerBottom + 12);
      const initialTop = this.layout.getBoundingClientRect().top + window.scrollY;
      const available = Math.max(80, window.innerHeight - Math.max(stickyTop, initialTop) - 16);
      const thumbnails = this.media.querySelector('.thumbnails-wrapper');
      const vertical = group.classList.contains('product-single__media-group__wrap--thumbnails-right');
      const thumbnailHeight = Math.min(120, Math.max(56, available * 0.22));
      this.setProperty('--sc-product-thumbnail-height', `${Math.floor(thumbnailHeight)}px`);
      const thumbnailStyle = thumbnails ? getComputedStyle(thumbnails) : null;
      const reserved = thumbnails && !vertical ? Math.min(thumbnails.offsetHeight, thumbnailHeight) + (parseFloat(thumbnailStyle.marginTop) || 0) : 0;
      const groupStyle = getComputedStyle(group);
      const imageWidth = Math.max(1, group.clientWidth - (parseFloat(groupStyle.paddingLeft) || 0) - (parseFloat(groupStyle.paddingRight) || 0));
      const ratio = (parseFloat(wrapper.style.getPropertyValue('--padding')) || 100) / 100;
      const imageHeight = Math.max(1, Math.floor(Math.min(imageWidth * ratio, available - reserved)));
      this.setProperty('--sc-product-sticky-top', `${stickyTop}px`);
      this.setProperty('--sc-product-main-height', `${imageHeight}px`);
    }

    revealThumbnail() {
      if (!desktop.matches || this.suspended) return;
      const wrapper = this.media.querySelector('.thumbnails-wrapper');
      const active = wrapper?.querySelector('.product-single__thumbnail.active-thumb');
      if (!active || wrapper.scrollHeight <= wrapper.clientHeight) return;
      const box = wrapper.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (item.top < box.top + 4) wrapper.scrollTop += item.top - box.top - 4;
      else if (item.bottom > box.bottom - 4) wrapper.scrollTop += item.bottom - box.bottom + 4;
    }

    suspend() {
      if (this.suspended) return;
      this.suspended = true;
      this.restoreContent();
      this.layout?.classList.remove('sc-product-desktop-layout');
      if (this.layout) this.clearProperties();
      this.resizeObserver.disconnect();
      this.thumbnailObserver.disconnect();
      window.removeEventListener('resize', this.queueMeasure);
      window.removeEventListener('scroll', this.queueMeasure);
      this.header?.removeEventListener('transitionend', this.queueMeasure);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    destroy() {
      if (!this.suspended) this.restoreContent(true);
      this.suspend();
      this.marker?.remove();
    }
  }

  function reconcile() {
    pending = 0;
    instances.forEach((instance, product) => {
      if (!product.isConnected) {
        instance.destroy();
        instances.delete(product);
      }
    });
    document.querySelectorAll('.product-page-section product-information[id^="MainProduct-"]').forEach(product => {
      if (!instances.has(product)) {
        const instance = new DesktopProductLayout(product);
        instances.set(product, instance);
        instance.connect();
      } else instances.get(product).update();
    });
  }

  window.SCProductDesktopLayout = { refresh };
  desktop.addEventListener('change', refresh);
  ['shopify:section:load', 'shopify:section:reorder'].forEach(name => document.addEventListener(name, refresh));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach(instance => {
      if (event.target.contains(instance.product)) instance.suspend();
    });
    refresh();
  });
  const observer = new MutationObserver(changes => {
    const selector = 'product-information, .product-page-section, .product-content-section';
    if (changes.some(change => [...change.addedNodes, ...change.removedNodes].some(node => node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))))) refresh();
  });
  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

if (!customElements.get('product-content')) {
  customElements.define('product-content', class ProductContent extends HTMLElement {
    constructor() {
      super();
      this.handleBuyTools();
      this.handleCollapsibleBlocks();
    }

    handleBuyTools() {
      window.SCProductDesktopLayout.refresh();
    }

    handleCollapsibleBlocks() {
      this.accordionStates = new Map();
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onAccordionClick = event => {
        const summary = event.target.closest('summary');
        const detail = summary?.parentElement;
        const state = this.accordionStates.get(detail);
        if (!state || summary !== state.summary || event.defaultPrevented) return;
        if (event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        this.stopAccordionViewport();
        if (!state.desired) this.keepAccordionVisible(detail, state);
        this.animateAccordion(detail, state, !state.desired);
      };
      this.onMotionChange = () => {
        if (this.motionPreference.matches) {
          this.accordionStates.forEach((state, detail) => this.finishAccordion(detail, state));
        }
      };
      this.contentObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const detail = entry.target.closest('.product__collapsible-details');
          const state = this.accordionStates.get(detail);
          if (!state || entry.target !== state.content) return;
          const size = entry.target.getBoundingClientRect();
          const changed = Math.abs(size.height - state.contentHeight) > 1 || Math.abs(size.width - state.contentWidth) > 1;
          if (state.animation && state.desired && changed) this.animateAccordion(detail, state, true);
          state.contentHeight = size.height;
          state.contentWidth = size.width;
        });
      });

      this.querySelectorAll('.product__collapsible-details').forEach(detail => {
        const summary = detail.querySelector(':scope > summary');
        const content = detail.querySelector(':scope > .product__collapsible__content');
        if (!summary || !content) return;
        const size = content.getBoundingClientRect();
        const state = {
          summary, content, desired: detail.open, animation: null, contentAnimation: null,
          initialHeight: detail.style.getPropertyValue('height'),
          initialHeightPriority: detail.style.getPropertyPriority('height'),
          initialInert: content.inert,
          contentHeight: size.height, contentWidth: size.width
        };
        state.onToggle = () => {
          if (state.animation && detail.open) return;
          if (state.animation) this.finishAccordion(detail, state, detail.open);
          state.desired = detail.open;
          detail.dataset.pcOpen = String(detail.open);
          summary.setAttribute('aria-expanded', String(detail.open));
          if (detail.open) this.closeOtherAccordions(detail);
        };
        detail.dataset.pcOpen = String(detail.open);
        summary.setAttribute('aria-expanded', String(detail.open));
        detail.addEventListener('toggle', state.onToggle);
        this.accordionStates.set(detail, state);
        this.contentObserver.observe(content);
      });
      let firstOpen = null;
      this.accordionStates.forEach((state, detail) => {
        if (!state.desired) return;
        if (firstOpen) this.finishAccordion(detail, state, false);
        else firstOpen = detail;
      });
      this.addEventListener('click', this.onAccordionClick);
      this.motionPreference.addEventListener('change', this.onMotionChange);
    }

    keepAccordionVisible(detail, state) {
      ProductContent.viewportOwner?.stopAccordionViewport();
      const root = document.documentElement;
      const box = state.summary.getBoundingClientRect();
      const viewport = {
        detail, state, root,
        header: document.querySelector('.shopify-section-header'),
        top: Math.min(box.top, window.innerHeight - box.height - Math.min(160, window.innerHeight * 0.25)),
        overflowAnchor: root.style.getPropertyValue('overflow-anchor'),
        overflowAnchorPriority: root.style.getPropertyPriority('overflow-anchor'),
        deadline: performance.now() + 1500,
        idle: false, frame: 0
      };
      this.accordionViewport = viewport;
      ProductContent.viewportOwner = this;
      // Compensate for collapsing siblings ourselves while this transition runs.
      root.style.setProperty('overflow-anchor', 'none');
      viewport.cancel = event => {
        if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
        this.stopAccordionViewport();
      };
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => {
        window.addEventListener(type, viewport.cancel, { capture: true, passive: true });
      });
      const follow = () => {
        if (this.accordionViewport !== viewport) return;
        if (!detail.isConnected || !state.desired || performance.now() > viewport.deadline) {
          this.stopAccordionViewport();
          return;
        }
        const safeTop = Math.max(12, (viewport.header?.getBoundingClientRect().bottom || 0) + 12);
        const targetTop = Math.max(safeTop, viewport.top);
        const delta = state.summary.getBoundingClientRect().top - targetTop;
        if (Math.abs(delta) > 0.5) window.scrollBy({ top: delta, behavior: 'instant' });
        const moving = [...this.accordionStates.values()].some(item => item.animation);
        // Keep one final frame after animation cleanup so natural height is measured.
        if (!moving && viewport.idle) {
          this.stopAccordionViewport();
          return;
        }
        viewport.idle = !moving;
        viewport.frame = requestAnimationFrame(follow);
      };
      viewport.frame = requestAnimationFrame(follow);
    }

    stopAccordionViewport() {
      const viewport = this.accordionViewport;
      if (!viewport) return;
      cancelAnimationFrame(viewport.frame);
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => {
        window.removeEventListener(type, viewport.cancel, true);
      });
      if (viewport.overflowAnchor) viewport.root.style.setProperty('overflow-anchor', viewport.overflowAnchor, viewport.overflowAnchorPriority);
      else viewport.root.style.removeProperty('overflow-anchor');
      this.accordionViewport = null;
      if (ProductContent.viewportOwner === this) ProductContent.viewportOwner = null;
    }

    closeOtherAccordions(activeDetail) {
      this.accordionStates.forEach((state, detail) => {
        if (detail !== activeDetail && state.desired) this.animateAccordion(detail, state, false);
      });
    }

    animateAccordion(detail, state, desired) {
      if (desired) this.closeOtherAccordions(detail);
      const height = detail.getBoundingClientRect().height;
      const wasOpen = detail.open;
      const contentStyle = getComputedStyle(state.content);
      const opacity = wasOpen ? contentStyle.opacity : '0';
      const transform = wasOpen ? contentStyle.transform : 'translateY(-4px)';
      this.cancelAccordionAnimations(state);
      state.desired = desired;
      detail.dataset.pcOpen = String(desired);
      state.summary.setAttribute('aria-expanded', String(desired));
      if (!desired && state.content.contains(document.activeElement)) state.summary.focus({ preventScroll: true });
      state.content.inert = desired ? state.initialInert : true;

      if (this.motionPreference.matches || typeof detail.animate !== 'function') {
        this.finishAccordion(detail, state);
        return;
      }

      detail.open = true;
      detail.style.height = 'auto';
      const detailStyle = getComputedStyle(detail);
      const border = parseFloat(detailStyle.borderTopWidth) + parseFloat(detailStyle.borderBottomWidth);
      const endHeight = desired ? detail.getBoundingClientRect().height : state.summary.getBoundingClientRect().height + border;
      const contentSize = state.content.getBoundingClientRect();
      state.contentHeight = contentSize.height;
      state.contentWidth = contentSize.width;
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      const configuredDuration = parseFloat(detailStyle.getPropertyValue('--pc-motion-duration'));
      const duration = Number.isFinite(configuredDuration) ? configuredDuration : mobile ? 260 : 300;
      detail.style.height = `${height}px`;
      const animation = detail.animate([
        { height: `${height}px` }, { height: `${endHeight}px` }
      ], { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' });
      state.animation = animation;
      state.contentAnimation = state.content.animate([
        { opacity, transform },
        { opacity: desired ? '1' : '0', transform: desired ? 'translateY(0)' : 'translateY(-4px)' }
      ], { duration: duration * (desired ? 0.9 : 0.75), easing: 'ease-out', fill: 'both' });
      animation.onfinish = () => {
        if (state.animation === animation) this.finishAccordion(detail, state);
      };
    }

    cancelAccordionAnimations(state) {
      if (state.animation) {
        state.animation.onfinish = null;
        state.animation.cancel();
        state.animation = null;
      }
      if (state.contentAnimation) {
        state.contentAnimation.cancel();
        state.contentAnimation = null;
      }
    }

    finishAccordion(detail, state, desired = state.desired) {
      state.desired = desired;
      detail.open = desired;
      this.cancelAccordionAnimations(state);
      if (state.initialHeight) detail.style.setProperty('height', state.initialHeight, state.initialHeightPriority);
      else detail.style.removeProperty('height');
      state.content.inert = state.initialInert;
      detail.dataset.pcOpen = String(desired);
      state.summary.setAttribute('aria-expanded', String(desired));
    }

    connectedCallback() {
      if (this.accordionDisconnected) {
        this.accordionDisconnected = false;
        this.handleCollapsibleBlocks();
      }
    }

    disconnectedCallback() {
      if (!this.accordionStates) return;
      this.stopAccordionViewport();
      this.contentObserver.disconnect();
      this.removeEventListener('click', this.onAccordionClick);
      this.motionPreference.removeEventListener('change', this.onMotionChange);
      this.accordionStates.forEach((state, detail) => {
        this.finishAccordion(detail, state);
        detail.removeEventListener('toggle', state.onToggle);
      });
      this.accordionStates.clear();
      this.accordionDisconnected = true;
    }
  });
}
